import React from 'react'
import { Navigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import ReuseConfirmationQueue from '../../components/compliance-reuse/ReuseConfirmationQueue'
import {
  fetchReuseOpportunities,
  fetchSharedControls,
  fetchSharedRemediation,
  confirmReuseCandidate,
  rejectReuseCandidate,
  type EvidenceReuseCandidate,
} from '../../services/complianceReuseApi'

type Tab = 'opportunities' | 'controls' | 'evidence' | 'remediation'

export default function ComplianceCenterCrossStandardReuseWorkspace() {
  const { t } = useI18n()
  const [tab, setTab] = React.useState<Tab>('opportunities')
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [opportunities, setOpportunities] = React.useState<EvidenceReuseCandidate[]>([])
  const [sharedControls, setSharedControls] = React.useState<Array<{ controlCode: string; standards: string[] }>>([])
  const [remediationImpacts, setRemediationImpacts] = React.useState<
    Array<{ title: string; affectedStandards: string[]; explanation?: { summary?: string } }>
  >([])

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [opp, ctrl, rem] = await Promise.all([
        fetchReuseOpportunities(),
        fetchSharedControls(),
        fetchSharedRemediation(),
      ])
      setOpportunities(opp.opportunities || [])
      setSharedControls(ctrl.sharedControls || opp.sharedControls || [])
      setRemediationImpacts(rem.remediationImpacts || opp.remediationImpacts || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load reuse data')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleConfirm = async (id: string) => {
    setBusy(true)
    try {
      await confirmReuseCandidate(id)
      setOpportunities((prev) => prev.filter((c) => c.id !== id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Confirm failed')
    } finally {
      setBusy(false)
    }
  }

  const handleReject = async (id: string) => {
    setBusy(true)
    try {
      await rejectReuseCandidate(id)
      setOpportunities((prev) => prev.filter((c) => c.id !== id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed')
    } finally {
      setBusy(false)
    }
  }

  if (!featureFlags.COMPLIANCE_REUSE_V1) {
    return <Navigate to="/compliance-center/overview" replace />
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'opportunities', label: t('complianceReuse.reuseOpportunities', 'Reuse opportunities') },
    { id: 'controls', label: t('complianceReuse.sharedControl', 'Shared controls') },
    { id: 'evidence', label: t('complianceReuse.sharedEvidence', 'Shared evidence') },
    { id: 'remediation', label: t('complianceReuse.sharedRemediation', 'Shared remediation') },
  ]

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('complianceCenterUi.nav.crossStandardReuse', 'Cross-standard reuse')}
        description={t(
          'complianceReuse.workspaceIntro',
          'Unified control universe — share evidence and remediation across standards with explicit confirmation.',
        )}
      >
        <div className="flex flex-wrap gap-2 mb-3">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                tab === item.id
                  ? 'bg-white border-blue-200 text-blue-800 font-medium shadow-sm'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {loading ? <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </GovernanceListingSection>

      {tab === 'opportunities' && !loading ? (
        <ReuseConfirmationQueue
          candidates={opportunities}
          busy={busy}
          onConfirm={handleConfirm}
          onReject={handleReject}
        />
      ) : null}

      {tab === 'controls' && !loading ? (
        <GovernanceListingSection title={t('complianceReuse.sharedControl', 'Shared controls')}>
          <ul className="space-y-2 m-0 p-0 list-none">
            {sharedControls.map((sc) => (
              <li key={sc.controlCode} className="text-xs border border-slate-100 rounded p-2 bg-slate-50">
                <span className="font-semibold">{sc.controlCode}</span>
                <span className="text-slate-500 ml-2">{sc.standards.join(' · ')}</span>
              </li>
            ))}
            {sharedControls.length === 0 ? (
              <li className="text-xs text-slate-500">{t('complianceReuse.noSharedControls', 'No cross-standard mappings yet.')}</li>
            ) : null}
          </ul>
        </GovernanceListingSection>
      ) : null}

      {tab === 'evidence' && !loading ? (
        <ReuseConfirmationQueue
          candidates={opportunities}
          busy={busy}
          onConfirm={handleConfirm}
          onReject={handleReject}
        />
      ) : null}

      {tab === 'remediation' && !loading ? (
        <GovernanceListingSection title={t('complianceReuse.sharedRemediationImpact', 'Shared remediation impact')}>
          <ul className="space-y-2 m-0 p-0 list-none">
            {remediationImpacts.map((r, i) => (
              <li key={i} className="text-xs border border-slate-100 rounded p-2 bg-slate-50">
                <p className="font-semibold m-0">{r.title}</p>
                <p className="text-slate-500 m-0 mt-1">{r.affectedStandards.join(' · ')}</p>
                {r.explanation?.summary ? <p className="text-slate-600 m-0 mt-1">{r.explanation.summary}</p> : null}
              </li>
            ))}
            {remediationImpacts.length === 0 ? (
              <li className="text-xs text-slate-500">{t('complianceReuse.noSharedRemediation', 'No shared remediation impacts detected.')}</li>
            ) : null}
          </ul>
        </GovernanceListingSection>
      ) : null}
    </div>
  )
}
